//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import moment from "moment-timezone";

import socialImageTemplate from '@partials/social-image.html';
import play24Image from '@images/play-24.svg';
import play60Image from '@images/play-60.svg';

import _ from "js/underscore-mixins";

/* global angular: false */

(function() {
  const curalate = angular.module('curalate');

    /*
     * This directive is meant to encapsulate common behaviors / styles used across images
     * on the dashboard. There's a notion of mixins here that allow the developer (through
     * simple attribution flags) to enabled or disabled certain features. For example, if
     * we wanted an image to have an 'Add to Library' button, we would...
     *
     * <social-image with-add-to-library
     *               tracking-category="MyTrackingName"
     *               src="image.src"
     *               metadata="image.metadata">
     * </social-image>
     *
     * @attr src               The source url for the image
     * @attr trackingCategory  The string used for any TrackingService calls
     * @attr metadata          An object containing any number of properties necessary to make the
     *                             mixed-in behavior work
     * @attr withX             When the social-image directive includes an attribute of this form, it
     *                             will enable behavior implied by the name (ie. withAddToLibrary will overlay
     *                             an 'Add to Library' button)
     *
     * The metadata attribute is a bit of a catchall for properties required or optional on mixins. The
     * 'with-add-to-library' mixin, for example, will look for a 'destinationUrl' and 'caption' property
     * on the metadata object.
     *
     */
  curalate.directive('socialImage', () => {
    return {
      restrict: 'E',
      scope: {
        trackingCategory: '@',
        src: '=',
        metadata: '=',
        withAddToLibrary: '@',
        withDownloadResource: '@',
        withClickThrough: '@',
        withNetworkIcon: '@',
        withPopover: '@',
        withCenteredImage: '@', // This requires that the element that contains social-image declares a height.
        withCoveredImage: '@', // Boolean to specify with to fill the image container. In other words, no borders.
        popoverPlacement: '@',
        popoverClass: '@',
        popoverTimeout: '@',
                // Options for UIA. Attach to scope so we can support pages with multiple item-typs
        overlayDisabled: '=',
        overlayItemType: '@',
        overlayItemId: '@',
        overlayPage: '@',
        uiaContext: '=',
        disableBulkActions: '<',
      },
      replace: true,
      transclude: true,
      template: socialImageTemplate,
      controller: [
        '$scope',
        '$attrs',
        '$sce',
        '$transclude',
        '$window',
        'ErrorNotificationService',
        'SchedulerService',
        'SessionVarsService',
        'ToastNotificationService',
        'TrackingService',
        function(
                    $scope,
                    $attrs,
                    $sce,
                    $transclude,
                    $window,
                    ErrorNotificationService,
                    SchedulerService,
                    SessionVarsService,
                    ToastNotificationService,
                    TrackingService,
                ) {
          const evalScopeVarThenAttrs = function(field) {
                            // If the attribute is on the scope, then it's an expression, which we evaluate as a boolean String (since it's @-bound).
                            // Otherwise, fall-back to evaluating the presence/absence of the attribute in the $attrs.
            return angular.isDefined($scope[field]) ? $scope[field] !== 'false' : angular.isDefined($attrs[field]);
          };

          $scope.popoverPlacement = $scope.popoverPlacement || 'left|top';    // With default
          $scope.popoverTimeout = $scope.popoverTimeout || 0;

                    // Scope bound functions and properties along with feature flags determined by attrs.
          $scope.model = {
            outstandingAsyncAction: null,
            overlay: {
              size: ($attrs.overlaySize && $attrs.overlaySize.length > 0) ? $attrs.overlaySize : 'medium',
              page: $scope.overlayPage || '',
            },
            features: {
              withAddToLibrary: evalScopeVarThenAttrs('withAddToLibrary'),
              withDownloadResource: evalScopeVarThenAttrs('withDownloadResource'),
              withClickThrough: evalScopeVarThenAttrs('withClickThrough'),
              withNetworkIcon: evalScopeVarThenAttrs('withNetworkIcon'),
              withPopover: evalScopeVarThenAttrs('withPopover'),
              withCenteredImage: evalScopeVarThenAttrs('withCenteredImage'),
              withCoveredImage: evalScopeVarThenAttrs('withCoveredImage'),
            },
            smallIcon: evalScopeVarThenAttrs('smallIcon'),
            theSmallIconImage: play24Image,
            theLargeIconImage: play60Image,
            isUiaEnabled: (SessionVarsService.isFeatureEnabled('universal_image_actions') && !$scope.overlayDisabled) ||
                            ($scope.uiaContext && $scope.uiaContext.forceEnableUia),
            context: {
              additionalLaunchOptions: {},
            },
          };

          $scope.$watch('overlayDisabled', () => {
            $scope.model.isUiaEnabled = (SessionVarsService.isFeatureEnabled('universal_image_actions') && !$scope.overlayDisabled) ||
                            ($scope.uiaContext && $scope.uiaContext.forceEnableUia);
          });

          this.$onInit = function() {
            if ($scope.metadata && $scope.metadata.reelId) {
              $scope.model.context.additionalLaunchOptions.selectedReel = {
                id: $scope.metadata.reelId,
                sharedByCreatorClientId: $scope.metadata.sharedByCreatorClientId,
                debuggerNote: 'This is not an actual reel instance.',
              };
            }
          };

          $scope.model.addToLibrary = function() {
            const timestamp = moment.utc().valueOf();
            const destinationUrl = $scope.metadata.destinationUrl;
            const source = $scope.metadata.hiResSource || $scope.src;
            let positionIncrement = 0;
            let productWithoutPosition = false;
            let captionWithAttribution;

            if ($scope.metadata.user && $scope.metadata.user.networkUsername) {
              captionWithAttribution = `${$scope.metadata.caption} via @${$scope.metadata.user.networkUsername}`;
            } else {
              captionWithAttribution = $scope.metadata.caption;
            }

            const productsData = _($scope.metadata.products).map((product) => {
              if (!product.position) {
                productWithoutPosition = true;
              }

              return {
                productStyleId: product.styleId,
                destinationUrl: product.url,
                id: product.productId,
                imageUrl: product.imageUrl,
                position: product.position || 1,
              };
            });

            if (productWithoutPosition) {
              _(productsData).each((product) => {
                positionIncrement++;
                product.position = positionIncrement;
              });
            }

            $scope.model.outstandingAsyncAction = 'AddToLibrary';

            SchedulerService.addSavedImageUrlAsync(source, destinationUrl, captionWithAttribution, productsData).then(() => {
              const scheduleImageUrl = `/publish/upload-and-schedule?timestamp=${timestamp}`;
              const toastArgs = {
                title: 'Awesome! Image was added to the Media Library.',
                message: `<a href="${scheduleImageUrl}">Schedule Image</a>`,
                options: {
                  allowHtml: true,
                },
              };
              TrackingService.recordEvent($scope.trackingCategory, TrackingService.ACTION_ADD_TO_LIBRARY);
              ToastNotificationService.success(toastArgs);
            }).catch(() => {
              const message = 'Failed to add image to Media Library.';
              ErrorNotificationService.showDefaultErrorDialog(message);
            }).finally(() => {
              $scope.model.outstandingAsyncAction = null;
            });
          };

          $scope.model.downloadResourceClick = function(e) {
                        // get the url of the item (image or video) to download
            let downloadUrl = $scope.src;
            if ($scope.metadata) {
              if ($scope.metadata.videoSource) {
                downloadUrl = $scope.metadata.videoSource;
              } else {
                downloadUrl = $scope.metadata.hiResSource;
              }
            }

                        // setup the anchor to auto-download it when clicked
            const $socialPopover = angular.element(e.currentTarget).closest('.social-popover');
            const $anchor = $socialPopover.find('.download-resource-button').get(0);
            $anchor.setAttribute('href', downloadUrl);
            $anchor.setAttribute('download', downloadUrl);
          };

          $scope.model.openDestinationUrl = function(url) {
            $window.open(url, '_blank');
          };

          $scope.model.playVideo = function(e) {
            const $socialPopover = angular.element(e.currentTarget).closest('.social-popover');
            const video = $socialPopover.find('video').get(0);
            const $playButton = $socialPopover.find('.video-icon');

            if (!(video.canPlayType && video.canPlayType('video/mp4').replace(/no/, ''))) {
                            // The browser does not support playing video - fallback to opening the destination URL
              $scope.model.openDestinationUrl($scope.metadata.destinationUrl);
              return;
            }

            if (video) {
              video.addEventListener('ended', () => {
                $playButton.show();
              });

              $playButton.hide();
              video.play();

              $scope.model.isVideoPlaying = true;
              TrackingService.recordEvent($scope.trackingCategory, TrackingService.ACTION_PLAY_VIDEO);
            }
          };

          $scope.model.stopVideo = function(e) {
            const $socialPopover = angular.element(e.currentTarget).closest('.social-popover');
            const video = $socialPopover.find('video').get(0);
            const $playButton = $socialPopover.find('.video-icon');

            if (!video || !video.readyState) {
              return;
            }

            video.pause();
            $playButton.show();
          };

          if ($scope.metadata && $scope.metadata.videoSource) {
            $scope.model.videoSource = $sce.trustAsResourceUrl($scope.metadata.videoSource);
          }
        },
      ],
      link($scope, $element) {
        $element.on('mouseenter', () => {
          $scope.model.loadVideo = true;
        });
      },
    };
  });
})();
